import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ViewStateMetadata } from '../../../../api/models/apis';
import { formatDate } from '../../../../components/miscUtils';
import styles from './ViewStateSelector.module.scss';
import { useState } from 'react';
import { useViewState } from '../../hooks/ViewStateContext';
import { showErrorMessage } from '../../../../components/message-boxes/ErrorMessage';
import { editViewStateForm } from './editViewStateForm';
import { promptAutoForm } from '../../../../components/auto-form-modal/AutoForm';
import { promptConfirmationBox } from '../../../../components/message-boxes/ConfirmBox';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { VISIBILITY_CONFIG, Visibility } from '../constants/visibility-config';
import { VIEW_STATE_TERMS } from '../../../../data/snapshot/dashboardStateSerialization';

interface ViewStateRowProps {
  viewState: ViewStateMetadata;
  isActive: boolean;
  setIsBusy: (isBusy: boolean) => void;
  close: () => void;
}

enum viewStateAction {
  Load,
  CopyLink,
  Edit,
  Delete,
}

interface ViewStateActionButtonProps {
  icon: IconProp;
  busyIcon?: IconProp;
  label?: string;
  busyLabel?: string;
  className?: string;
  isBusy: boolean;
  onClick: () => void;
  ariaLabel?: string;
  title?: string;
  disabledWhenBusy?: boolean;
}

function ViewStateActionButton({
  icon,
  busyIcon = ['fas', 'spinner'],
  label = '',
  busyLabel = '',
  className,
  isBusy,
  onClick,
  ariaLabel,
  title,
  disabledWhenBusy = true,
}: ViewStateActionButtonProps) {
  return (
    <button
      type="button"
      className={`${styles.iconButton} ${className ?? ''}`}
      disabled={isBusy && disabledWhenBusy}
      onClick={onClick}
      aria-label={ariaLabel}
      title={title}
    >
      <FontAwesomeIcon icon={isBusy ? busyIcon : icon} spin={isBusy && !!busyIcon && disabledWhenBusy} />
      {isBusy ? busyLabel : label}
    </button>
  );
}

export function ViewStateRow({ viewState, isActive, setIsBusy, close }: ViewStateRowProps) {
  const { loadViewStateFromServer, updateViewState, deleteViewState } = useViewState();

  const [busyAction, setBusyAction] = useState<viewStateAction | null>(null);
  const [isCopied, setIsCopied] = useState(false);

  const updateBusyAction = (action: viewStateAction | null) => {
    setBusyAction(action);
  };

  async function onCopyLink() {
    const link = `${window.location.origin}${window.location.pathname}?viewState=${viewState.id}`;
    try {
      await navigator.clipboard.writeText(link);
      setIsCopied(true);
      // Keep the success state visible long enough to notice without delaying another action.
      window.setTimeout(() => setIsCopied(false), 2000);
    } catch {
      showErrorMessage(`Unable to copy link to clipboard. Copy link manually: ${link}`);
    }
  }

  async function onStateEdit() {
    updateBusyAction(viewStateAction.Edit);
    try {
      await promptAutoForm(editViewStateForm({ viewState, updateViewState }));
    } catch {
      return;
    } finally {
      updateBusyAction(null);
    }
  }

  async function onStateRemove() {
    updateBusyAction(viewStateAction.Delete);
    try {
      await promptConfirmationBox({
        title: `Delete saved ${VIEW_STATE_TERMS.singular}?`,
        description: `This will permanently delete the ${VIEW_STATE_TERMS.singular} <strong>${viewState.name}</strong>.`,
        warningAlert: true,
      });
      await deleteViewState(viewState.id);
    } catch (error) {
      if (error) showErrorMessage(error instanceof Error ? error.message : String(error));
    } finally {
      updateBusyAction(null);
    }
  }

  async function onStateLoad() {
    try {
      updateBusyAction(viewStateAction.Load);
      setIsBusy(true);
      const loadStatus = await loadViewStateFromServer(viewState.id);

      // Close the modal if the view state was successfully loaded
      if (loadStatus === 'success') {
        close();
      }
    } catch (error) {
      if (error) showErrorMessage(error instanceof Error ? error.message : String(error));
    } finally {
      updateBusyAction(null);
      setIsBusy(false);
    }
  }

  // Statuses
  const isLoading = busyAction === viewStateAction.Load;
  const isEditing = busyAction === viewStateAction.Edit;
  const isDeleting = busyAction === viewStateAction.Delete;
  const createdBy = viewState.isOwn ? 'You' : viewState.createdBy || 'Unknown';
  const visibilityConfig = VISIBILITY_CONFIG[viewState.visibility as Visibility];

  return (
    <article className={styles.stateRow}>
      <div className={styles.stateContent}>
        <div className={styles.stateName}>
          <strong>{viewState.name}</strong>
          {isActive && (
            <span className={styles.activeBadge}>
              (<FontAwesomeIcon icon={['fas', 'check']} /> Active)
            </span>
          )}
          <FontAwesomeIcon
            icon={visibilityConfig.icon}
            title={visibilityConfig.tooltip}
            aria-label={visibilityConfig.ariaLabel}
          />
        </div>
        <div className={styles.metadata}>
          Created by {createdBy} &bull; {formatDate(viewState.createdAt)}
        </div>
        {viewState.description && <div className={styles.description}>{viewState.description}</div>}
      </div>
      <div className={styles.actions}>
        <ViewStateActionButton
          icon={['fas', 'folder-open']}
          isBusy={isLoading}
          onClick={onStateLoad}
          ariaLabel={`Load ${viewState.name}`}
          title="Load"
        />
        <ViewStateActionButton
          icon={['fas', 'clipboard']}
          isBusy={isCopied}
          busyIcon={['fas', 'clipboard-check']}
          onClick={onCopyLink}
          ariaLabel={`Copy link for ${viewState.name}`}
          title="Copy link"
          disabledWhenBusy={false}
        />
        {viewState.canEdit && (
          <>
            <ViewStateActionButton
              icon={['fas', 'pencil']}
              isBusy={isEditing}
              onClick={onStateEdit}
              ariaLabel={`Edit ${viewState.name}`}
              title="Edit"
            />
            <ViewStateActionButton
              icon={['fas', 'trash-can']}
              isBusy={isDeleting}
              onClick={onStateRemove}
              ariaLabel={`Delete ${viewState.name}`}
              title="Delete"
            />
          </>
        )}
      </div>
    </article>
  );
}
